import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import RepresentativeDocumentsTab from "./tabs/RepresentativeDocumentsTab";
import RepresentativeProfileForm from "../../pages/Representatives/RepresentativeProfileForm";
import DropdownMenu from "../../components/controls/DropdownMenu";
import Button from "../../components/controls/Button";
import Tabs from "../../components/controls/Tabs";

export default function RepresentativeEditModal({
  rep,
  onClose,
  onSave,
  allRepresentatives = [],
}) {
  const { t } = useTranslation();
  const [activeTabIndex, setActiveTabIndex] = useState(0);
  const [form, setForm] = useState({ parentId: rep.parentId || null });
  const [loadingParent, setLoadingParent] = useState(false);

  const getDescendantIds = (representatives, currentId) => {
    const descendants = new Set();
    const recurse = (id) => {
      representatives.forEach((r) => {
        if (r.parentId === id) {
          descendants.add(r.id);
          recurse(r.id);
        }
      });
    };
    recurse(currentId);
    return descendants;
  };

  const descendantIds = useMemo(() => {
    return rep.id ? getDescendantIds(allRepresentatives, rep.id) : new Set();
  }, [allRepresentatives, rep.id]);

  const eligibleParents = useMemo(() => {
    return (allRepresentatives || []).filter(
      (r) => r.id !== rep.id && !descendantIds.has(r.id)
    );
  }, [allRepresentatives, descendantIds, rep.id]);

  const selectedParentLabel = useMemo(() => {
    if (!form.parentId) return t("Aucun parrain");
    const found = (allRepresentatives || []).find(
      (r) => r.id === form.parentId
    );
    if (!found) return t("Aucun parrain");
    return `${found.firstName || ""} ${found.lastName || ""} (${
      found.code || ""
    })`.trim();
  }, [allRepresentatives, form.parentId, t]);

  const handleParentSelect = async (newParentId) => {
    setForm((prev) => ({ ...prev, parentId: newParentId }));
    setLoadingParent(true);

    try {
      const body = newParentId ?? "00000000-0000-0000-0000-000000000000";
      const res = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/Representative/${rep.id}/assign-parent`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(body),
        }
      );

      if (!res.ok) {
        setForm((prev) => ({ ...prev, parentId: rep.parentId || null }));
        return;
      }

      onSave?.();
    } finally {
      setLoadingParent(false);
    }
  };

  const parentItems = useMemo(() => {
    const base = [
      {
        key: "none",
        label: t("Aucun parrain"),
        onClick: () => handleParentSelect(null),
      },
    ];

    const opts = eligibleParents.map((r) => ({
      key: r.id,
      label: `${r.firstName || ""} ${r.lastName || ""} (${
        r.code || ""
      })`.trim(),
      onClick: () => handleParentSelect(r.id),
    }));

    return [...base, ...opts];
  }, [eligibleParents, t]);

  const tabs = useMemo(
    () => [
      { key: "profile", label: t("Profil") },
      { key: "clients", label: t("Clients") },
      { key: "subscriptions", label: t("Abonnements") },
      { key: "parrainage", label: t("Parrainage") },
      { key: "documents", label: t("Documents") },
    ],
    [t]
  );

  const activeTabKey = tabs[activeTabIndex]?.key || "profile";

  const renderParrainageTab = () => (
    <div className="text-gray-600 border border-ananas rounded-tr-lg rounded-b-lg p-6">
      <p className="mb-2 font-medium">
        {form.parentId
          ? `${t("Parrain actuel :")} ${selectedParentLabel}`
          : t("Aucun parrainage pour ce représentant.")}
      </p>

      <div className="mt-4">
        <span className="block mb-2 font-semibold">
          {t("Parrain (optionnel)")}
        </span>

        <div className="w-full">
          <DropdownMenu
            title={loadingParent ? t("loading") : selectedParentLabel}
            items={parentItems}
            isDropdown
            withSearch
            searchPlaceholder={t("Rechercher...")}
          />
        </div>

        <div className="mt-3 flex justify-end">
          <Button
            type="button"
            onClick={() => handleParentSelect(null)}
            color="primary"
            size="sm"
            disabled={loadingParent}
          >
            {t("Retirer le parrain")}
          </Button>
        </div>
      </div>
    </div>
  );

  const renderContent = () => {
    if (activeTabKey === "profile") {
      return (
        <RepresentativeProfileForm
          userId={rep.userId}
          onSaved={onSave}
          showIntro={false}
          showTitle={false}
          compact
          saveButtonLabel={t("global.save")}
        />
      );
    }

    if (activeTabKey === "clients") {
      return (
        <div className="text-gray-600 border border-ananas rounded-tr-lg rounded-b-lg p-6">
          <p>
            {t(
              "La liste des clients du représentant sera affichée ici. (à venir)"
            )}
          </p>
        </div>
      );
    }

    if (activeTabKey === "subscriptions") {
      return (
        <div className="text-gray-600 border border-ananas rounded-tr-lg rounded-b-lg p-6">
          <p>
            {t(
              "Les abonnements vendus par ce représentant apparaîtront ici. (à venir)"
            )}
          </p>
        </div>
      );
    }

    if (activeTabKey === "parrainage") return renderParrainageTab();

    return <RepresentativeDocumentsTab repId={rep.id} repUserId={rep.userId} />;
  };

  return (
    <div className="">
      <h2 className="text-xl font-bold mb-2">
        {t("Modifier le représentant")}
      </h2>

      <Tabs
        tabs={tabs}
        activeIndex={activeTabIndex}
        onChange={setActiveTabIndex}
      />
      <div className="min-h-[80%]">{renderContent()}</div>
    </div>
  );
}
